/**
 * Hook for fetching navigation item counts (inbox, starred, later, drafts).
 * Provides counts to display badges next to nav items.
 * Uses Zustand store so counts can be updated from anywhere in the app.
 */

import { useEffect, useCallback } from 'react';

import { fetchNavCounts } from './navCountsApi';
import { useAppStore } from '../../stores/useAppStore';


const DEFAULT_REFRESH_INTERVAL = 30_000; // 30 seconds
const SSE_REFRESH_INTERVAL = 120_000; // 2 minutes (when SSE is active)

/**
 * Fetches and periodically refreshes navigation badge counts — inbox, starred,
 * later and drafts — with one request per tick. Counts are stored in the global Zustand app store
 * so they can be displayed as badges on sidebar navigation items.
 *
 * Polling frequency adapts based on SSE connectivity: 30 seconds when polling
 * is the primary update mechanism, 2 minutes when SSE is active and provides
 * real-time push updates. A change in SSE state only changes the cadence; it
 * never triggers an immediate fetch. Polling stops entirely while the document
 * is hidden (the SSE stream also drops on hide), and a single fetch fires when
 * it becomes visible again before the interval resumes. Also re-fetches
 * immediately when an SSE-triggered email refresh event fires.
 *
 * @returns The current navigation counts object (`emailInbox`, `starredInbox`,
 *   `readLaterInbox`, `drafts`).
 */
export function useNavCounts() {
  const navCounts = useAppStore(state => state.navCounts);
  const setNavCounts = useAppStore(state => state.setNavCounts);
  const emailRefreshTrigger = useAppStore(state => state.emailRefreshTrigger);
  const sseConnected = useAppStore(state => state.sseConnected);

  const fetchCounts = useCallback(async () => {
    try {
      const counts = await fetchNavCounts();
      setNavCounts({
        emailInbox: counts.inbox,
        starredInbox: counts.starred,
        readLaterInbox: counts.later,
        drafts: counts.drafts,
      });
    } catch (error) {
      console.error('Failed to fetch nav counts:', error);
      // Keep previous counts on error
    }
  }, [setNavCounts]);

  // Refetch when SSE triggers a refresh
  useEffect(() => {
    if (emailRefreshTrigger > 0) {
      void fetchCounts();
    }
  }, [emailRefreshTrigger, fetchCounts]);

  // Fetch once on mount, and again whenever the tab becomes visible.
  // Kept separate from the interval so cadence changes never refetch.
  useEffect(() => {
    void fetchCounts();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void fetchCounts();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchCounts]);

  // Periodic refresh: paused while hidden, slower when SSE is connected.
  // A cadence or visibility change only restarts the timer, never fetches.
  useEffect(() => {
    const delay = sseConnected ? SSE_REFRESH_INTERVAL : DEFAULT_REFRESH_INTERVAL;
    let interval: ReturnType<typeof setInterval> | null = null;
    const stop = () => {
      if (interval !== null) clearInterval(interval);
      interval = null;
    };
    const sync = () => {
      stop();
      if (document.visibilityState !== 'visible') return;
      interval = setInterval(() => void fetchCounts(), delay);
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      stop();
    };
  }, [fetchCounts, sseConnected]);

  return navCounts;
}
